import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  initializeSupportChat,
  loadOlderSupportMessages,
  markSupportThreadRead,
  removeLocalMessage,
  selectSupportAgentTyping,
  selectSupportCacheHydrated,
  selectSupportError,
  selectSupportHasMore,
  selectSupportLoadingMessages,
  selectSupportLoadingOlder,
  selectSupportLoadingThread,
  selectSupportMessages,
  selectSupportSending,
  selectSupportSyncingMessages,
  selectSupportThread,
  selectSupportThreadInitialized,
  sendSupportMessageThunk,
} from '../../store/supportChatSlice';
import { selectAuth } from '../../store/authSlice';
import { selectIsSocketConnected } from '../../store/socketSlice';
import type { SupportLocalMessage, SupportSubmitPayload } from '../../types/support';
import SupportMessageBubble from './SupportMessageBubble';
import SupportChatInput from './SupportChatInput';
import SupportIcon from '../icons/SupportIcon';

interface Props {
  onClose: () => void;
  /**
   * Вариант отображения:
   *  - `popup`  — плавающая панель в углу (по умолчанию).
   *  - `page`   — вписанный в страницу режим (без кнопки закрытия и без анимации появления).
   */
  variant?: 'popup' | 'page';
}

const GROUP_GAP_MS = 5 * 60 * 1000;

const formatDateLabel = (iso: string): string => {
  try {
    const d = new Date(iso);
    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (isToday) return 'Сегодня';

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday =
      d.getFullYear() === yesterday.getFullYear() &&
      d.getMonth() === yesterday.getMonth() &&
      d.getDate() === yesterday.getDate();
    if (isYesterday) return 'Вчера';

    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return '';
  }
};

const isSameDay = (a: string, b: string): boolean => {
  try {
    const da = new Date(a);
    const db = new Date(b);
    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  } catch {
    return false;
  }
};

interface RenderedItem {
  type: 'date' | 'message';
  key: string;
  date?: string;
  message?: SupportLocalMessage;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  showTimestamp?: boolean;
}

const buildRenderItems = (messages: SupportLocalMessage[]): RenderedItem[] => {
  const items: RenderedItem[] = [];
  let lastDate: string | null = null;
  for (let i = 0; i < messages.length; i += 1) {
    const m = messages[i];
    const prev = messages[i - 1] ?? null;
    const next = messages[i + 1] ?? null;

    if (!lastDate || !isSameDay(lastDate, m.createdAt)) {
      items.push({ type: 'date', key: `date-${m.createdAt}`, date: m.createdAt });
      lastDate = m.createdAt;
    }

    const startGroup =
      !prev ||
      prev.direction !== m.direction ||
      !isSameDay(prev.createdAt, m.createdAt) ||
      Math.abs(new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime()) > GROUP_GAP_MS;
    const endGroup =
      !next ||
      next.direction !== m.direction ||
      !isSameDay(next.createdAt, m.createdAt) ||
      Math.abs(new Date(next.createdAt).getTime() - new Date(m.createdAt).getTime()) > GROUP_GAP_MS;

    items.push({
      type: 'message',
      key: m.localId,
      message: m,
      isFirstInGroup: startGroup,
      isLastInGroup: endGroup,
      showTimestamp: false,
    });
  }
  return items;
};

const SupportChatWindow: React.FC<Props> = ({ onClose, variant = 'popup' }) => {
  const isPageVariant = variant === 'page';
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const thread = useAppSelector(selectSupportThread);
  const messages = useAppSelector(selectSupportMessages);
  const hasMore = useAppSelector(selectSupportHasMore);
  const loadingThread = useAppSelector(selectSupportLoadingThread);
  const loadingMessages = useAppSelector(selectSupportLoadingMessages);
  const loadingOlder = useAppSelector(selectSupportLoadingOlder);
  const sending = useAppSelector(selectSupportSending);
  const initialized = useAppSelector(selectSupportThreadInitialized);
  const cacheHydrated = useAppSelector(selectSupportCacheHydrated);
  const syncingMessages = useAppSelector(selectSupportSyncingMessages);
  const isAgentTyping = useAppSelector(selectSupportAgentTyping);
  const error = useAppSelector(selectSupportError);
  const isSocketConnected = useAppSelector(selectIsSocketConnected);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const lightboxUrlRef = useRef<string | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Bootstrap на странице /support (виджет там скрыт, но чат — основной контент).
  useEffect(() => {
    if (user?.userId) {
      dispatch(initializeSupportChat(user.userId));
    }
  }, [user?.userId, dispatch]);

  // markRead при открытии и при появлении новых
  useEffect(() => {
    if (thread && thread.unreadForUser > 0) {
      dispatch(markSupportThreadRead());
    }
  }, [thread?.unreadForUser, dispatch, thread]);

  // авто-скролл вниз при добавлении новых сообщений (если уже у дна)
  const previousLengthRef = useRef(0);
  const wasAtBottomRef = useRef(true);
  const initialScrollDoneRef = useRef(false);

  // При смене треда сбрасываем состояние авто-скролла, чтобы при следующей
  // загрузке истории заново уехать вниз.
  useEffect(() => {
    initialScrollDoneRef.current = false;
    previousLengthRef.current = 0;
    wasAtBottomRef.current = true;
  }, [thread?.id]);

  // Авто-скролл при добавлении новых сообщений, если пользователь у дна.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    const wasAtBottom = wasAtBottomRef.current;
    const lengthIncreased = messages.length > previousLengthRef.current;
    if (lengthIncreased && wasAtBottom) {
      container.scrollTop = container.scrollHeight;
    }
    previousLengthRef.current = messages.length;
  }, [messages]);

  // Первичный скролл к самому низу: после того как история загрузилась.
  // Прокручиваем сразу, а затем ещё раз в следующем кадре — к этому моменту
  // браузер успевает разметить контент (без учёта ещё не загруженных картинок;
  // их изменения высоты подхватит ResizeObserver ниже).
  useLayoutEffect(() => {
    if (initialScrollDoneRef.current) return;
    if (loadingMessages && !cacheHydrated) return;
    if (messages.length === 0) return;
    const container = scrollRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
    requestAnimationFrame(() => {
      const c = scrollRef.current;
      if (c) c.scrollTop = c.scrollHeight;
    });
    initialScrollDoneRef.current = true;
  }, [loadingMessages, cacheHydrated, messages.length]);

  // Держим скролл у дна, пока контент «вырастает» из-за асинхронной подгрузки
  // (картинки в сообщениях, шрифты и т.п.). Если пользователь самостоятельно
  // прокрутил вверх — `wasAtBottomRef.current` станет false, и авто-прижатие
  // отключится.
  useEffect(() => {
    const container = scrollRef.current;
    const list = messagesRef.current;
    if (!container || !list) return;
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (wasAtBottomRef.current) {
        container.scrollTop = container.scrollHeight;
      }
    });
    ro.observe(list);
    return () => ro.disconnect();
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const threshold = 60;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    wasAtBottomRef.current = distanceFromBottom < threshold;

    // infinite scroll up
    if (container.scrollTop < 80 && hasMore && !loadingOlder && messages.length > 0) {
      const prevHeight = container.scrollHeight;
      const prevTop = container.scrollTop;
      dispatch(loadOlderSupportMessages())
        .unwrap()
        .then(() => {
          // сохраняем позицию скролла
          requestAnimationFrame(() => {
            const c = scrollRef.current;
            if (!c) return;
            const newHeight = c.scrollHeight;
            c.scrollTop = newHeight - prevHeight + prevTop;
          });
        })
        .catch(() => {});
    }
  }, [hasMore, loadingOlder, messages.length, dispatch]);

  const handleSubmit = useCallback(
    async (payload: SupportSubmitPayload) => {
      const hasContent =
        payload.text.trim().length > 0 ||
        payload.attachments.length > 0 ||
        !!payload.voice;
      if (!hasContent) return;
      wasAtBottomRef.current = true;
      await dispatch(sendSupportMessageThunk(payload));
    },
    [dispatch],
  );

  const handleRetry = useCallback(
    async (localId: string) => {
      const target = messages.find((m) => m.localId === localId);
      if (!target) return;
      // удалить failed-сообщение и попробовать заново
      const text = target.text || '';
      // повторно отправить без файлов (исходные File-объекты не сохранены, к сожалению)
      // покажем уведомление, что файлы надо прикрепить заново
      dispatch(removeLocalMessage(localId));
      if (target.pendingAttachments && target.pendingAttachments.length > 0) {
        await dispatch(sendSupportMessageThunk({ text, attachments: [] }));
      } else {
        await dispatch(sendSupportMessageThunk({ text, attachments: [] }));
      }
    },
    [dispatch, messages],
  );

  const handleDeleteFailed = useCallback(
    (localId: string) => {
      dispatch(removeLocalMessage(localId));
    },
    [dispatch],
  );

  const handleOpenLightbox = useCallback((url: string) => {
    lightboxUrlRef.current = url;
    setLightboxUrl(url);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxUrl(null);
    lightboxUrlRef.current = null;
  }, []);

  // Esc -> закрыть лайтбокс или окно (только в popup-варианте; на странице Esc не уходим со страницы).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxUrlRef.current) {
          setLightboxUrl(null);
          lightboxUrlRef.current = null;
        } else if (!isPageVariant) {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, isPageVariant]);

  const renderItems = useMemo(() => buildRenderItems(messages), [messages]);

  const isClosed = thread?.status === 'closed';
  const placeholder = isClosed
    ? 'Этот тред закрыт. Отправьте сообщение, чтобы открыть новый.'
    : thread
      ? 'Опишите проблему...'
      : 'Здравствуйте! Опишите вашу проблему — мы поможем.';

  const isInitialLoading =
    loadingThread && !initialized && messages.length === 0 && !cacheHydrated;

  return (
    <div
      className={`sc-window ${isPageVariant ? 'sc-window--page' : 'sc-window--popup'}`}
      role={isPageVariant ? 'region' : 'dialog'}
      aria-label="Чат с поддержкой"
    >
      <header className="sc-header">
        <div className="sc-header__main">
          <div className="sc-header__avatar" aria-hidden="true">
            <SupportIcon size={22} strokeWidth={2} />
          </div>
          <div className="sc-header__text">
            <div className="sc-header__title">Поддержка</div>
            <div className="sc-header__subtitle">
              <span className={`sc-status-dot ${isSocketConnected ? 'is-online' : 'is-offline'}`} aria-hidden="true" />
              {syncingMessages
                ? 'Обновление истории...'
                : isSocketConnected
                  ? 'Онлайн'
                  : 'Соединение с сервером...'}
            </div>
          </div>
        </div>
        {!isPageVariant && (
          <button
            type="button"
            className="sc-header__close"
            onClick={onClose}
            aria-label="Свернуть чат"
            title="Свернуть"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </header>

      <div
        className="sc-messages"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {isInitialLoading && (
          <div className="sc-state sc-state--loading">
            <div className="sc-spinner-lg" aria-hidden="true" />
            <span>Загружаем чат...</span>
          </div>
        )}

        {!isInitialLoading && !thread && messages.length === 0 && (
          <div className="sc-state sc-state--empty">
            <div className="sc-state__icon" aria-hidden="true">
              <SupportIcon size={40} strokeWidth={1.5} />
            </div>
            <h3>Здравствуйте!</h3>
            <p>
              Мы здесь, чтобы помочь. Опишите вашу проблему и при необходимости приложите
              скриншоты — обычно мы отвечаем в течение 15 минут.
            </p>
          </div>
        )}

        {!isInitialLoading && thread && messages.length === 0 && !loadingMessages && !cacheHydrated && (
          <div className="sc-state sc-state--empty">
            <div className="sc-state__icon" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3>История пуста</h3>
            <p>Отправьте первое сообщение, чтобы начать диалог.</p>
          </div>
        )}

        {loadingOlder && (
          <div className="sc-loading-older" aria-label="Загрузка предыдущих сообщений">
            <div className="sc-spinner-sm" />
          </div>
        )}

        <div className="sc-messages__list" ref={messagesRef}>
          {renderItems.map((item) => {
            if (item.type === 'date') {
              return (
                <div className="sc-date-sep" key={item.key}>
                  <span>{formatDateLabel(item.date!)}</span>
                </div>
              );
            }
            return (
              <SupportMessageBubble
                key={item.key}
                message={item.message!}
                isFirstInGroup={!!item.isFirstInGroup}
                isLastInGroup={!!item.isLastInGroup}
                showTimestamp={!!item.showTimestamp}
                onRetry={handleRetry}
                onDelete={handleDeleteFailed}
                onOpenLightbox={handleOpenLightbox}
              />
            );
          })}
        </div>

        {isAgentTyping && (
          <div className="sc-typing" aria-live="polite" aria-label="Поддержка печатает">
            <span className="sc-typing__dot" />
            <span className="sc-typing__dot" />
            <span className="sc-typing__dot" />
          </div>
        )}

        {error && !loadingMessages && (
          <div className="sc-error-banner" role="alert">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}
      </div>

      {isClosed && (
        <div className="sc-closed-banner">
          Обращение закрыто. Отправьте сообщение, чтобы открыть новое.
        </div>
      )}

      <SupportChatInput
        disabled={false}
        sending={sending}
        placeholder={placeholder}
        onSubmit={handleSubmit}
      />

      {lightboxUrl && (
        <div
          className="sc-lightbox"
          role="dialog"
          aria-label="Просмотр изображения"
          onClick={handleCloseLightbox}
        >
          <img src={lightboxUrl} alt="" onClick={(e) => e.stopPropagation()} />
          <button
            type="button"
            className="sc-lightbox__close"
            onClick={handleCloseLightbox}
            aria-label="Закрыть"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default SupportChatWindow;
